import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  fetchInstagramConversation,
  fetchInstagramConversations,
  sendInstagramConversationReply,
  updateInstagramConversation,
} from '../../services/organisation.service';
import {
  IInstagramConversation,
  IInstagramMessage,
  TInstagramConversationStatus,
} from '../../types/@organisation';

const unwrap = <T,>(response: any): T => (response?.data || response) as T;

const OrganisationMessagesPanel = ({ organisationId }: { organisationId: string }): ReactElement => {
  const [status, setStatus] = useState<TInstagramConversationStatus>('open');
  const [conversations, setConversations] = useState<IInstagramConversation[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState<IInstagramMessage[]>([]);
  const [selected, setSelected] = useState<IInstagramConversation | null>(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const loadList = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      const payload = unwrap<any>(await fetchInstagramConversations(organisationId, status));
      setConversations(payload.conversations || []);
      if (!selectedId && payload.conversations?.length) setSelectedId(payload.conversations[0]._id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Instagram conversations could not be loaded.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [organisationId, selectedId, status]);

  const loadThread = useCallback(async (conversationId: string, quiet = false) => {
    if (!conversationId) return;
    try {
      if (!quiet) setLoading(true);
      const payload = unwrap<any>(await fetchInstagramConversation(organisationId, conversationId));
      setSelected(payload.conversation);
      setMessages(payload.messages || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'The Instagram conversation could not be loaded.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [loadThread, selectedId]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      loadList(true);
      if (selectedId) loadThread(selectedId, true);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadList, loadThread, selectedId]);

  const windowText = useMemo(() => {
    if (!selected?.replyWindowExpiresAt) return 'No active reply window';
    const expiry = new Date(selected.replyWindowExpiresAt);
    return expiry.getTime() > Date.now()
      ? `Human-agent reply window ends ${expiry.toLocaleString()}`
      : 'Human-agent reply window expired';
  }, [selected]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!selectedId || !text) return;
    try {
      setSending(true);
      setError('');
      await sendInstagramConversationReply(organisationId, selectedId, text);
      setReply('');
      await loadThread(selectedId, true);
      await loadList(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'The Instagram reply could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (next: TInstagramConversationStatus) => {
    if (!selectedId) return;
    await updateInstagramConversation(organisationId, selectedId, { status: next });
    setSelectedId('');
    setSelected(null);
    setMessages([]);
    await loadList();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#17324d' }}>Instagram messages</Typography>
          <Typography sx={{ color: '#5f7285' }}>Club staff can read and manually reply to customer-initiated Instagram conversations.</Typography>
        </Box>
        <Select size="small" value={status} onChange={(e) => { setSelectedId(''); setStatus(e.target.value as TInstagramConversationStatus); }}>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="spam">Spam</MenuItem>
        </Select>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {loading && !conversations.length ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, minHeight: 520, border: '1px solid #e3e8ef', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ borderRight: { md: '1px solid #e3e8ef' }, maxHeight: 650, overflowY: 'auto' }}>
            {!conversations.length && <Typography sx={{ p: 3, color: '#6b7c8f' }}>No {status} Instagram conversations.</Typography>}
            {conversations.map((conversation) => (
              <Box key={conversation._id} onClick={() => setSelectedId(conversation._id)} sx={{ p: 2, cursor: 'pointer', background: selectedId === conversation._id ? '#eef6ff' : '#fff', borderBottom: '1px solid #edf0f4' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#17324d' }}>{conversation.participantName || `Instagram user ${conversation.participantId.slice(-6)}`}</Typography>
                  {!!conversation.unreadCount && <Chip size="small" label={conversation.unreadCount} color="primary" />}
                </Box>
                <Typography noWrap sx={{ mt: 0.5, color: '#5f7285', fontSize: 14 }}>{conversation.lastMessagePreview}</Typography>
                <Typography sx={{ mt: 0.5, color: '#8a98a8', fontSize: 12 }}>{conversation.lastMessageAt ? new Date(conversation.lastMessageAt).toLocaleString() : ''}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {!selected ? <Typography sx={{ p: 4, color: '#6b7c8f' }}>Select a conversation.</Typography> : <>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 700 }}>{selected.participantName || `Instagram user ${selected.participantId}`}</Typography><Typography sx={{ fontSize: 12, color: '#6b7c8f' }}>{windowText}</Typography></Box>
                <Select size="small" value={selected.status} onChange={(e) => changeStatus(e.target.value as TInstagramConversationStatus)}>
                  <MenuItem value="open">Open</MenuItem><MenuItem value="closed">Closed</MenuItem><MenuItem value="spam">Spam</MenuItem>
                </Select>
              </Box>
              <Divider />
              <Box sx={{ p: 2, flex: 1, maxHeight: 480, overflowY: 'auto', background: '#f7f9fc' }}>
                {messages.map((message) => <Box key={message.messageId} sx={{ display: 'flex', justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start', mb: 1.5 }}><Box sx={{ maxWidth: '75%', p: 1.5, borderRadius: 2, background: message.direction === 'outbound' ? '#dceeff' : '#fff', border: '1px solid #e3e8ef' }}><Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text || 'Attachment'}</Typography><Typography sx={{ mt: 0.5, fontSize: 11, color: '#7b8998' }}>{new Date(message.sentAt).toLocaleString()}</Typography></Box></Box>)}
              </Box>
              <Divider />
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField fullWidth multiline maxRows={5} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a manual reply…" inputProps={{ maxLength: 1000 }} />
                <Button variant="contained" disabled={sending || !reply.trim() || (!!selected.replyWindowExpiresAt && new Date(selected.replyWindowExpiresAt).getTime() <= Date.now())} onClick={sendReply}>{sending ? 'Sending…' : 'Send'}</Button>
              </Box>
            </>}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default OrganisationMessagesPanel;
